import {
  Anchor,
  Avatar,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons';

import { REDACTED_FIELD } from '../config/logrocket';
import { formatPhoneAsString } from '../i18n/forms/LanguageForm';
import { useCowbellTranslations } from '../i18n/translations';
import { initialsOf } from './myAgent.styles';

export interface InterestedAgencyRow {
  readonly id?: string;
  readonly name?: string;
  readonly contactName?: string;
  readonly contactEmail?: string;
  readonly contactPhone?: string;
  readonly city?: string;
  readonly state?: string;
}

interface InterestedAgenciesProps {
  readonly loading?: boolean;
  readonly interestedAgencies?: readonly InterestedAgencyRow[];
}

function HeaderCell({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <Table.Th>
      <Text size="xs" fw={600} tt="uppercase" c="dimmed">
        {children}
      </Text>
    </Table.Th>
  );
}

/**
 * Agencies that have registered interest in this account. Read-only: the
 * make-my-agent action went with the link/unlink flow (FIR-21113), so this is a
 * contact list rather than a picker.
 */
export const InterestedAgencies = ({
  loading,
  interestedAgencies = [],
}: InterestedAgenciesProps) => {
  const { t, i18n } = useCowbellTranslations();

  if (loading) {
    return (
      <Stack gap="xs" p="md">
        <Skeleton height={20} />
        <Skeleton height={20} />
        <Skeleton height={20} />
      </Stack>
    );
  }

  if (interestedAgencies.length === 0) {
    return (
      <Stack gap={4} align="center" p="xl">
        <Text fw={600}>No agencies are interested in your account yet</Text>
        <Text size="sm" c="dimmed">
          Agencies that want to work with you will appear here.
        </Text>
      </Stack>
    );
  }

  return (
    <Table.ScrollContainer minWidth={860}>
      <Table highlightOnHover verticalSpacing="sm" className={REDACTED_FIELD}>
        <Table.Thead>
          <Table.Tr>
            <HeaderCell>{`${t('agency')} Name`}</HeaderCell>
            <HeaderCell>Contact Name</HeaderCell>
            <HeaderCell>Contact Email</HeaderCell>
            <HeaderCell>Contact Phone</HeaderCell>
            <HeaderCell>City / State</HeaderCell>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {interestedAgencies.map((row) => (
            <Table.Tr key={row.id ?? row.name}>
              <Table.Td>
                <Group gap="sm" wrap="nowrap">
                  <Avatar radius="xl" size={28} color="blue">
                    {initialsOf(row.name)}
                  </Avatar>
                  <Text fw={600} size="sm">
                    {row.name}
                  </Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{row.contactName || '—'}</Text>
              </Table.Td>
              <Table.Td>
                {row.contactEmail ? (
                  <Anchor href={`mailto:${row.contactEmail}`} size="sm">
                    {row.contactEmail}
                  </Anchor>
                ) : (
                  <Text size="sm">—</Text>
                )}
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {row.contactPhone
                    ? formatPhoneAsString(
                        i18n.language as any,
                        row.contactPhone
                      )
                    : '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap={6} wrap="nowrap">
                  <Text c="blue" span>
                    <FontAwesomeIcon icon={faLocationDot} />
                  </Text>
                  <Text size="sm" c="dimmed">
                    {[row.city, row.state].filter(Boolean).join(', ') || '—'}
                  </Text>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
};

export default InterestedAgencies;
